import { Link, useRouter } from '@tanstack/react-router';
import { AlertTriangle, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Per-route error component, wired as ``defaultErrorComponent`` on the
 * root route. Differs from the top-level ``ErrorBoundary``:
 *
 *   - Renders INSIDE the AppShell, so the navbar + subnav stay visible
 *     and the user can navigate away without a full reload.
 *   - Fires for errors thrown by a route's ``loader`` / ``beforeLoad`` /
 *     ``component`` — render-phase TanStack Router catches these on a
 *     per-route basis and replaces the matched route's output here.
 *
 * Render-phase crashes that escape the route boundary still hit the
 * top-level ``ErrorBoundary``. The two are complementary.
 *
 * Copy is intentionally friendly — no stack traces, no module paths,
 * no "SyntaxError" jargon. Customers see a calm "something didn't
 * load" message + Retry + Return to Dashboard. Developers can still
 * read the underlying error in the browser console (``console.error``
 * fires from TanStack Router automatically when a route throws).
 */
export function RouteErrorComponent({
  error,
  reset,
}: {
  error: Error;
  reset?: () => void;
}) {
  const router = useRouter();

  // Surface the raw error in the dev console so engineers can debug,
  // but never leak it to the rendered UI. ``console.error`` already
  // fires from TanStack Router on the throw — this is a safety net so
  // the message is present even if a future refactor changes that.
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.error('[RouteErrorComponent]', error);
  }

  const handleRetry = () => {
    // Clear the route's error state then ask the router to re-run loaders.
    // ``reset`` is provided by TanStack Router when available.
    reset?.();
    void router.invalidate();
  };

  return (
    <main className="max-w-[720px] mx-auto px-6 pt-12 pb-20">
      <section className="bg-white border border-line rounded-2xl shadow-card p-8 text-center">
        <div className="mx-auto size-12 rounded-full bg-state-warning-bg flex items-center justify-center mb-5">
          <AlertTriangle className="size-6 text-state-warning-accent" aria-hidden />
        </div>
        <h2 className="m-0 mb-2 text-[20px] font-bold text-navy">
          We couldn&rsquo;t load this page
        </h2>
        <p className="text-[14px] leading-[1.55] text-muted m-0 mb-6">
          Something went wrong on our end. Try again in a moment, or head back to
          your dashboard. The rest of the app is still available from the navbar.
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <Button type="button" variant="outline" onClick={handleRetry}>
            <RefreshCcw className="size-3.5" /> Try again
          </Button>
          <Button type="button" variant="brand" asChild>
            <Link to="/dashboard">Return to Dashboard</Link>
          </Button>
        </div>
      </section>
    </main>
  );
}
